import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { CreditCard, Truck, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

export default function PaymentModal({ open, onClose, pkg, user }) {
  const [step, setStep] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    if (!deliveryAddress.trim()) {
      setError("Por favor, introduce tu dirección de entrega");
      return;
    }
    setError("");
    setStep(2);
  };

  const handlePayWithStripe = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para continuar");
      return;
    }

    setLoading(true);
    try {
      // Get origin URL dynamically (NEVER hardcode)
      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/packages/checkout`, {
        package_id: pkg.package_id,
        delivery_address: deliveryAddress,
        origin_url: originUrl
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Error al procesar el pago");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setDeliveryAddress("");
    setError("");
    setLoading(false);
    onClose();
  };

  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {step === 1 ? "Dirección de Entrega" : "Confirmar Pago"}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? "Introduce tu dirección para recibir el producto"
              : "Revisa los detalles y procede al pago seguro"
            }
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="address">Dirección completa</Label>
              <Textarea
                id="address"
                placeholder="Calle, número, piso, código postal, ciudad..."
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                className="mt-1.5 min-h-[100px]"
                data-testid="delivery-address-input"
              />
              {error && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {error}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <Truck className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                El envío está incluido en el precio. Solo se enviará si se confirma la compra grupal.
              </p>
            </div>

            <Button 
              className="w-full" 
              onClick={handleNext}
              data-testid="continue-to-payment-btn"
            >
              Continuar al pago
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Order Summary */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex gap-3">
                <img 
                  src={pkg.product_image} 
                  alt={pkg.product_name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div>
                  <h4 className="font-medium text-sm">{pkg.product_name}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {pkg.product_condition === 'new' ? 'Nuevo' : 
                     pkg.product_condition === 'used' ? 'Usado' : 'Reacondicionado (CPO)'}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Precio original</span>
                  <span className="line-through">{pkg.original_price.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Descuento grupal</span>
                  <span className="text-green-600">-{(pkg.original_price - pkg.group_price).toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Envío</span>
                  <span className="text-green-600">Gratis</span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span className="text-lg">{pkg.group_price.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <span className="font-medium text-sm">Pago seguro</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Tarjeta de crédito/débito, Apple Pay, Google Pay
              </p>
            </div>

            {/* Guarantee */}
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                <strong>Garantía de devolución:</strong> Si no se alcanza el mínimo de participantes, recuperarás tu dinero automáticamente.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                Atrás
              </Button>
              <Button 
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={handlePayWithStripe}
                disabled={loading}
                data-testid="confirm-payment-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  `Pagar ${pkg.group_price.toFixed(2)}€`
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
